import type {
  PartnerCompaniesQueryParams,
  PaginatedPartnerCompanyList,
} from './types';

import {
  keepPreviousData,
  queryOptions,
  useQuery,
  type DefaultError,
  type QueryFunctionContext,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { queryKeysDataExchange } from '../queryKeys';

import { fetchPartnerCompanies } from './fetchPartnerCompanies';

/**
 * Generates the query options for the partner companies list query.
 *
 * @param queryParams - Optional filter and pagination parameters.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Exchange/operation/get_partner_companies_data_exchange_companies_get
 */
export const getPartnerCompaniesQueryOptions = <
  TData = PaginatedPartnerCompanyList,
>({
  queryParams = {},
  options,
}: {
  queryParams?: PartnerCompaniesQueryParams;
  options?: Omit<
    UseQueryOptions<PaginatedPartnerCompanyList, DefaultError, TData>,
    'queryKey' | 'queryFn'
  >;
} = {}) =>
  queryOptions({
    queryFn: async (queryContext: QueryFunctionContext) =>
      fetchPartnerCompanies(queryParams, queryContext),
    queryKey: queryKeysDataExchange.getPartnerCompanies(queryParams),
    staleTime: ms('60s'),
    ...options,
  });

/**
 * React Query based custom hook for fetching the paginated list of partner companies.
 *
 * @param queryParams - Optional filter and pagination parameters.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Exchange/operation/get_partner_companies_data_exchange_companies_get
 */
export const useQueryPartnerCompanies = <TData = PaginatedPartnerCompanyList>(
  queryParams?: PartnerCompaniesQueryParams,
  options?: Omit<
    UseQueryOptions<PaginatedPartnerCompanyList, DefaultError, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery({
    ...getPartnerCompaniesQueryOptions<TData>({ queryParams, options }),
    placeholderData: keepPreviousData,
  });
